import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { post, get, safeNext } from "@/lib/api";
import { LOGIN } from "@/constants/testIds";
import { startGoogle, NEXT_KEY } from "@/pages/AuthCallback";

const ALLOWED = ["dhwaniris.com", "trif.in"]; // mirrors the server; the server is the authority

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

function Msg({ text, ok, testid }) {
  if (!text) return null;
  return <div className={`msg ${ok ? "ok" : "err"}`} role="alert" aria-live="polite" data-testid={testid}>{text}</div>;
}

export default function Login() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const next = safeNext(params.get("next"));
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);
  const [codeMsg, setCodeMsg] = useState(null);
  const [left, setLeft] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => { get("/auth/me").then((r) => r.ok && nav(next + window.location.hash, { replace: true })); }, []); // eslint-disable-line
  useEffect(() => { if (left <= 0) return; const t = setTimeout(() => setLeft(left - 1), 1000); return () => clearTimeout(t); }, [left]);

  async function sendCode() {
    const v = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+$/.test(v)) { setEmailMsg({ text: "Enter your work email address." }); return false; }
    if (!ALLOWED.includes(v.split("@")[1])) { setEmailMsg({ text: "Only @dhwaniris.com and @trif.in email addresses can sign in." }); return false; }
    setBusy(true); setEmailMsg(null);
    try {
      const r = await post("/auth/request-otp", { email: v });
      if (!r.ok) { setEmailMsg({ text: r.data.error || "Could not send the code. Try again." }); return false; }
      setEmail(v); setCode(""); setStep("code"); setLeft(r.data.resend_after || 30); setCodeMsg(null);
      setTimeout(() => codeRef.current && codeRef.current.focus(), 50);
      return true;
    } catch (e) { setEmailMsg({ text: "Network error. Check your connection and try again." }); return false; }
    finally { setBusy(false); }
  }

  async function verify(c) {
    if (c.length !== 6) { setCodeMsg({ text: "Enter the 6-digit code from the email." }); return; }
    setBusy(true);
    try {
      const r = await post("/auth/verify", { email, code: c });
      if (r.ok) { setCodeMsg({ text: "Signed in. Opening the dashboard…", ok: true }); window.location.replace(next + (window.location.hash || "")); return; }
      setCodeMsg({ text: r.data.error || "That code didn't work." });
    } catch (e) { setCodeMsg({ text: "Network error. Try again." }); }
    finally { setBusy(false); }
  }

  async function resend() {
    const ok = await sendCode();
    setStep("code");
    setCodeMsg(ok ? { text: "A new code is on its way. Earlier codes no longer work.", ok: true } : emailMsg);
  }

  const onCode = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(v);
    if (v.length === 6 && !busy) verify(v);
  };

  return (
    <main className="login-shell">
      <section className="login-card" aria-labelledby="login-h">
        <div className="brand"><img className="brand-logo" src="/tri-logo.png" alt="Transform Rural India" data-testid="login-brand-logo" /><span className="sep" aria-hidden="true" /><div><b>Grants &amp; Funds</b><span>Dashboard</span></div></div>
        {step === "email" ? (
          <form data-testid={LOGIN.emailForm} noValidate onSubmit={(e) => { e.preventDefault(); sendCode(); }}>
            <h1 id="login-h">Sign in</h1>
            <p className="lead">Only @dhwaniris.com or @trif.in accounts can sign in.</p>
            <button type="button" className="google" data-testid={LOGIN.googleButton}
              onClick={() => { try { sessionStorage.setItem(NEXT_KEY, next + window.location.hash); } catch (e) { /* storage off */ } startGoogle(); }}>
              <GoogleMark /> Continue with Google
            </button>
            <div className="or"><span>or get a code by email</span></div>
            <label htmlFor="email">Work email</label>
            <input id="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="off" spellCheck="false"
              placeholder="name@trif.in" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus data-testid={LOGIN.emailInput} />
            <button type="submit" className="primary" disabled={busy} data-testid={LOGIN.sendButton}>{busy ? "Sending…" : "Email me a code"}</button>
            <Msg {...emailMsg} testid={LOGIN.emailMessage} />
          </form>
        ) : (
          <form data-testid={LOGIN.codeForm} noValidate onSubmit={(e) => { e.preventDefault(); verify(code); }}>
            <h1 id="login-h">Check your email</h1>
            <p className="sent">We sent a 6-digit code to <b data-testid={LOGIN.sentTo}>{email}</b>. It expires in 10 minutes.</p>
            <label htmlFor="code">Sign-in code</label>
            <input id="code" ref={codeRef} className="code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
              placeholder="••••••" value={code} onChange={onCode} data-testid={LOGIN.codeInput} />
            <button type="submit" className="primary" disabled={busy} data-testid={LOGIN.verifyButton}>{busy ? "Signing in…" : "Sign in"}</button>
            <div className="row">
              <button type="button" className="link" onClick={() => { setStep("email"); setLeft(0); }} data-testid={LOGIN.backButton}>← Use a different email</button>
              <button type="button" className="link" disabled={left > 0 || busy} onClick={resend} data-testid={LOGIN.resendButton}>
                {left > 0 ? `Resend code in ${left}s` : "Resend code"}
              </button>
            </div>
            <Msg {...codeMsg} testid={LOGIN.codeMessage} />
          </form>
        )}
        <p className="hint">Trouble signing in? Ask your dashboard administrator. Codes come from the TRI Dashboard sender, so check your spam folder too.</p>
      </section>
    </main>
  );
}
